import * as html2img from 'html-to-image';
import { createMutation } from '@tanstack/svelte-query';
import { useSupabaseClient } from '~/lib/SupabaseProvider.svelte';
import type { Options } from 'html-to-image/lib/types';

// Helper to convert ArrayBuffer to Base64 string
const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

export const uploadThumbMutation = () => {
  const supabaseClient = useSupabaseClient();
  return createMutation(() => ({
    mutationFn: async (args: { blob: Blob; id: string }) => {
      const bucket = supabaseClient.storage.from('share-thumbnails');
      const res = await bucket.upload(`${args.id}.png`, args.blob, {
        contentType: 'image/png',
      });

      if (res.error) {
        throw new Error(res.error.message);
      }
      return { path: `${args.id}.png` };
    },
  }));
};

export const generateThumbMutation = () =>
  createMutation(() => ({
    mutationFn: async (args: { domRef: HTMLDivElement }) => {
      let customFontEmbedCSS = '';

      try {
        // Step 1: Fetch Google Fonts CSS for Poppins, including various weights and styles.
        // This URL requests the CSS that contains @font-face rules with URLs to the actual font files.
        const googleFontsCssRes = await fetch(
          'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
        );
        const googleFontsCssText = await googleFontsCssRes.text();

        // Step 2 & 3: Extract font URLs from the fetched CSS and fetch their binaries for base64 encoding.
        const fontFaceRegex = /@font-face\s*{([^}]+)}/g;
        let match;
        const fontPromises: Promise<void>[] = [];

        // Iterate through each @font-face block found in the Google Fonts CSS
        while ((match = fontFaceRegex.exec(googleFontsCssText)) !== null) {
          const fontFaceBlock = match[1];
          // Regex to find src: url(...) within the @font-face block, specifically targeting woff2 files.
          // woff2 is generally preferred for modern web and smaller size.
          const srcUrlRegex =
            /src:\s*url\(['"]?(https:\/\/[^)'"]+\.woff2)['"]?\)/g;
          let urlMatch;

          // Iterate through each URL found in the current @font-face block
          while ((urlMatch = srcUrlRegex.exec(fontFaceBlock)) !== null) {
            const fontUrl = urlMatch[1];

            fontPromises.push(
              (async () => {
                try {
                  const fontRes = await fetch(fontUrl);
                  const fontArrayBuffer = await fontRes.arrayBuffer();
                  const base64Font = arrayBufferToBase64(fontArrayBuffer);

                  // Extract font-family, font-weight, and font-style from the original @font-face block
                  const fontFamilyMatch =
                    /font-family:\s*['"]?([^;'"]+)['"]?/.exec(fontFaceBlock);
                  const fontWeightMatch = /font-weight:\s*(\d+)/.exec(
                    fontFaceBlock,
                  );
                  const fontStyleMatch = /font-style:\s*(\w+)/.exec(
                    fontFaceBlock,
                  );

                  if (fontFamilyMatch && fontWeightMatch && fontStyleMatch) {
                    // Step 4: Reconstruct the @font-face rule with the base64 encoded font data.
                    // This embeds the font directly into the CSS, bypassing external URL fetching issues for html-to-image.
                    customFontEmbedCSS += `
@font-face {
  font-family: '${fontFamilyMatch[1]}';
  font-weight: ${fontWeightMatch[1]};
  font-style: ${fontStyleMatch[1]};
  src: url(data:font/woff2;base64,${base64Font}) format('woff2');
  font-display: swap;
}`;
                  }
                } catch (fontFetchError) {
                  console.error(
                    'Failed to fetch or process font:',
                    fontUrl,
                    fontFetchError,
                  );
                }
              })(),
            );
          }
        }
        await Promise.all(fontPromises); // Wait for all font fetches and processing to complete

        if (customFontEmbedCSS.trim() === '') {
          console.warn(
            'No custom font-embed CSS was generated. Falling back to html2img.getFontEmbedCSS.',
          );
          // If custom CSS generation failed, fall back to html-to-image's default font embedding
          customFontEmbedCSS = await html2img.getFontEmbedCSS(args.domRef);
        }
      } catch (error) {
        console.error('Error generating custom font embed CSS:', error);
        // Fallback in case of any error during custom CSS generation process
        customFontEmbedCSS = await html2img.getFontEmbedCSS(args.domRef);
      }

      const params: Options = {
        width: 1200,
        height: 630,
        pixelRatio: 1,
        style: { display: 'block' },
        fontEmbedCSS: customFontEmbedCSS, // Provide the dynamically generated base64 font CSS
      };

      return {
        png: await html2img.toPng(args.domRef, params),
        blob: await html2img.toBlob(args.domRef, params),
      };
    },
  }));
